import React, { useMemo, useState } from "react";

import {
  ITransferInfoLineProps,
  OwnershipTransferDialog,
} from "components/OwnershipTransferDialog";
import { Menu, MenuItem } from "components/shared";
import useDownload from "components/shared/Download";
import {
  BED_FILE,
  BOM_GENERATOR,
  CTF_FILE,
  GTF_FILE,
  PRODUCT_INSERT,
  QUOTES,
} from "helpers/apis";
import useAxios from "helpers/axios";
import useAuth from "hooks/useAuth";
import useQuote from "hooks/useQuote";
import { IQuote } from "types/quote";
import { IUser, OrgUser, UserRole } from "types/user";

interface IQuoteMenuProps {
  anchorEl: HTMLElement | null;
  selectedQuote: IQuote;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
}

export const QuoteMenu: React.FC<IQuoteMenuProps> = ({
  anchorEl,
  selectedQuote,
  setAnchorEl,
}) => {
  const { hasPermission, user } = useAuth();
  const isAdmin = hasPermission([UserRole.Admin]);
  const isSalesRep = hasPermission([UserRole.SalesRep]);
  const { download } = useDownload({ requestConfig: {} });
  const { setPanelTransferred, setQuotes } = useQuote();
  const { fetchData } = useAxios({
    config: {
      url: `${QUOTES}${selectedQuote.id}`,
      method: "PUT",
    },
    callOnInit: false,
  });

  const infoLines: ITransferInfoLineProps[] = useMemo(
    () => [
      {
        label: "Panel ID",
        infoText: selectedQuote.mpId.toString().padStart(5, "0"),
      },
      {
        label: "Panel Name",
        infoText: selectedQuote.panelName,
      },
      {
        label: "Current Owner",
        infoText: (
          <>
            {selectedQuote.user?.firstName} {selectedQuote.user?.lastName}
            <br />
            {selectedQuote.user?.email}
          </>
        ),
      },
    ],
    [selectedQuote]
  );

  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const canTransferOwnership = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (isSalesRep && selectedQuote.user.id === user?.id) {
      return true;
    }
    return false;
  }, [selectedQuote, isAdmin, isSalesRep, user]);

  const transferPanelOwnership = (
    newOwner: OrgUser,
    setError: (error: string) => void
  ) => {
    fetchData(
      {
        data: {
          user: newOwner?.id,
        },
      },
      () => {
        setPanelTransferred(true);
        setOpenTransferDialog(false);
        setQuotes((quotes) => [
          ...quotes.map((item) => {
            if (item.id === selectedQuote.id) {
              return {
                ...item,
                user: newOwner as any as IUser,
              };
            }
            return item;
          }),
        ]);
      },
      ({ error }: { error: string }) => {
        setError(error);
      }
    );
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
        paperWidth={160}
      >
        <MenuItem
          size="sm"
          onClick={() => {
            download({
              url: GTF_FILE,
              params: !!selectedQuote.customPanel
                ? { customPanelId: selectedQuote.customPanel }
                : { designId: selectedQuote.design },
            });
          }}
        >
          GTF
        </MenuItem>
        <MenuItem
          size="sm"
          onClick={() => {
            download({
              url: BED_FILE,
              params: !!selectedQuote.customPanel
                ? { customPanelId: selectedQuote.customPanel }
                : { designId: selectedQuote.design },
            });
          }}
        >
          BED
        </MenuItem>
        {!!selectedQuote.customPanel && (
          <MenuItem
            size="sm"
            onClick={() => {
              download({
                url: `${PRODUCT_INSERT}${selectedQuote.customPanel}`,
              });
            }}
          >
            Product Insert
          </MenuItem>
        )}
        {!!selectedQuote.customPanel && (isAdmin || isSalesRep) && (
          <MenuItem
            size="sm"
            onClick={() => {
              download({
                url: CTF_FILE,
                params: {
                  customPanelId: selectedQuote.customPanel,
                },
              });
            }}
          >
            CTF
          </MenuItem>
        )}
        {!!selectedQuote.customPanel && (isAdmin || isSalesRep) && (
          <MenuItem
            size="sm"
            onClick={() => {
              download({
                url: `${BOM_GENERATOR}${selectedQuote.customPanel}`,
              });
            }}
          >
            BOM
          </MenuItem>
        )}
        {canTransferOwnership && (
          <MenuItem size="sm" onClick={() => setOpenTransferDialog(true)}>
            Transfer Ownership
          </MenuItem>
        )}
      </Menu>
      {canTransferOwnership && (
        <OwnershipTransferDialog
          infos={infoLines}
          onTransfer={transferPanelOwnership}
          openDialog={openTransferDialog}
          setOpenDialog={setOpenTransferDialog}
        />
      )}
    </>
  );
};
